// @ts-nocheck

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/components/ui/ToastProvider";
import { formatTimeAgo } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { IconShield, IconCheck, IconX, IconFlag, IconLoader } from "@/components/ui/Icons";

interface Report {
  id: string;
  target_id: string;
  target_type: "post" | "comment" | "user";
  reason: string;
  description: string | null;
  status: string;
  created_at: string;
  reporter_id: string;
  users: {
    id: string; username: string; display_name: string;
    avatar_url: string | null; karma_score: number;
  } | null;
}

interface ModerationQueueClientProps {
  reports: Report[];
  moderatorId: string;
}

type ActionType = "dismiss" | "remove" | "warn" | "ban_24h" | "ban_7d" | "ban_perm";

const REASON_LABELS: Record<string, string> = {
  spam: "Spam",
  harassment: "Harassment",
  misinformation: "Misinformation",
  nsfw_unlabeled: "Unlabeled NSFW",
  violence: "Violence",
  other: "Other",
};

const REASON_COLORS: Record<string, "destructive" | "warning" | "muted"> = {
  spam: "muted",
  harassment: "destructive",
  misinformation: "warning",
  nsfw_unlabeled: "warning",
  violence: "destructive",
  other: "muted",
};

export function ModerationQueueClient({ reports: initial, moderatorId }: ModerationQueueClientProps) {
  const [reports, setReports] = useState<Report[]>(initial);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | Report["target_type"]>("all");
  const { success, error: showError } = useToast();

  const filtered = filter === "all" ? reports : reports.filter(r => r.target_type === filter);

  const handleAction = async (reportId: string, targetId: string, targetType: string, action: ActionType) => {
    setProcessingId(reportId);
    try {
      const res = await fetch(`/api/admin/reports/${reportId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, target_id: targetId, target_type: targetType }),
      });

      if (!res.ok) throw new Error("Action failed");

      setReports(prev => prev.filter(r => r.id !== reportId));
      success("Action taken", `Report ${action === "dismiss" ? "dismissed" : "resolved"}.`);
    } catch {
      showError("Action failed", "Please try again.");
    } finally {
      setProcessingId(null);
    }
  };

  const filterCounts = {
    all: reports.length,
    post: reports.filter(r => r.target_type === "post").length,
    comment: reports.filter(r => r.target_type === "comment").length,
    user: reports.filter(r => r.target_type === "user").length,
  };

  return (
    <div className="space-y-4">
      {/* Filter tabs */}
      <div className="flex gap-2">
        {(["all", "post", "comment", "user"] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={cn("px-3 py-1.5 rounded-xl text-sm font-medium capitalize transition-colors flex items-center gap-1.5",
              filter === f ? "bg-brand text-white" : "bg-muted text-text-muted hover:bg-muted/80"
            )}>
            {f}
            <span className={cn("text-2xs px-1.5 py-0.5 rounded-full",
              filter === f ? "bg-white/20" : "bg-surface-overlay")}>
              {filterCounts[f]}
            </span>
          </button>
        ))}
      </div>

      {/* Queue items */}
      {filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 rounded-3xl bg-success/10 flex items-center justify-center mx-auto mb-4">
            <IconShield className="w-8 h-8 text-success" />
          </div>
          <p className="text-lg font-semibold text-foreground">Queue is clear</p>
          <p className="text-sm text-text-muted mt-1">No pending reports in this category</p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((report, i) => (
              <motion.div key={report.id}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12, height: 0 }}
                transition={{ delay: i * 0.03, duration: 0.2 }}
                className="card overflow-hidden">
                {/* Report header */}
                <div className="p-4 flex items-start gap-3">
                  {/* Reporter */}
                  <div className="flex-shrink-0">
                    <Avatar src={report.users?.avatar_url} alt={report.users?.display_name || "?"} size="sm" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant={REASON_COLORS[report.reason] || "muted"} size="sm">
                        {REASON_LABELS[report.reason] || report.reason}
                      </Badge>
                      <Badge variant="muted" size="sm" className="capitalize">
                        {report.target_type}
                      </Badge>
                      <span className="text-xs text-text-subtle">{formatTimeAgo(report.created_at)}</span>
                    </div>

                    <p className="text-sm text-text-muted">
                      Reported by{" "}
                      <Link to={`/profile/${report.users?.username}`} className="text-brand hover:underline">
                        @{report.users?.username}
                      </Link>
                      {report.description && (
                        <span className="text-text-subtle"> — &ldquo;{report.description.slice(0, 120)}&rdquo;</span>
                      )}
                    </p>

                    {/* Target link */}
                    <Link to={report.target_type === "post" ? `/post/${report.target_id}` : report.target_type === "user" ? `/profile/${report.target_id}` : `/post/${report.target_id}`}
                      target="_blank"
                      className="text-xs text-brand hover:underline mt-1 inline-block"
                    >
                      View {report.target_type} →
                    </Link>
                  </div>

                  {/* Quick actions */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {processingId === report.id ? (
                      <IconLoader className="w-5 h-5 text-text-muted" />
                    ) : (
                      <>
                        <button
                          onClick={() => handleAction(report.id, report.target_id, report.target_type, "dismiss")}
                          title="Dismiss report"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-text-subtle hover:text-success hover:bg-success/10 transition-colors"
                        >
                          <IconCheck className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleAction(report.id, report.target_id, report.target_type, "remove")}
                          title="Remove content"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-text-subtle hover:text-destructive hover:bg-destructive/10 transition-colors"
                        >
                          <IconX className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setExpandedId(expandedId === report.id ? null : report.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-text-subtle hover:text-foreground hover:bg-muted transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded actions */}
                <AnimatePresence>
                  {expandedId === report.id && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      className="overflow-hidden border-t border-border"
                    >
                      <div className="p-4 bg-surface-overlay flex flex-wrap gap-2">
                        <p className="w-full text-xs font-semibold text-text-muted mb-2">Author actions:</p>
                        {[
                          { action: "warn" as ActionType, label: "Issue warning", color: "btn-secondary" },
                          { action: "ban_24h" as ActionType, label: "Ban 24h", color: "btn-secondary" },
                          { action: "ban_7d" as ActionType, label: "Ban 7 days", color: "btn-secondary" },
                          { action: "ban_perm" as ActionType, label: "Permanent ban", color: "btn-destructive" },
                        ].map(({ action, label, color }) => (
                          <button
                            key={action}
                            onClick={() => handleAction(report.id, report.target_id, report.target_type, action)}
                            className={cn("btn-sm", color)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
